import type {
  ApprovalSummaryResult,
  CreateOTRuleDTO,
  CreateOvertimeRequestDTO,
  ListOTRulesQuery,
  ListOTRulesResult,
  ListOvertimeRequestsQuery,
  ListOvertimeRequestsResult,
  OTRuleDetailDTO,
  OTSummaryByMonthResult,
  OvertimeRequestDetailDTO,
  OvertimeRequestSummaryResult,
  UpdateOTRuleDTO,
  UpdateOvertimeRequestDTO,
} from "@/modules/hr/overtime/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = `${config.apiUrl}/hr/overtime`;

export const overtimeApi = {
  listRules(query: ListOTRulesQuery): Promise<ListOTRulesResult> {
    const params = new URLSearchParams();
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.sort != null) params.set("sort", JSON.stringify(query.sort));
    if (query.dayType != null) params.set("dayType", query.dayType);
    if (query.isActive != null) params.set("isActive", String(query.isActive));
    if (query.search != null) params.set("search", query.search);
    const q = params.toString();
    return fetcher.get<ListOTRulesResult>(q ? `${base}/rules?${q}` : `${base}/rules`);
  },

  getRuleById(id: string): Promise<OTRuleDetailDTO> {
    return fetcher.get<OTRuleDetailDTO>(`${base}/rules/${id}`);
  },

  createRule(dto: CreateOTRuleDTO): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(`${base}/rules`, dto);
  },

  updateRule(id: string, dto: UpdateOTRuleDTO): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/rules/${id}`, dto);
  },

  listRequests(query: ListOvertimeRequestsQuery): Promise<ListOvertimeRequestsResult> {
    const params = new URLSearchParams();
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.sort != null) params.set("sort", JSON.stringify(query.sort));
    if (query.mine != null) params.set("mine", String(query.mine));
    if (query.periodYear != null) params.set("periodYear", String(query.periodYear));
    if (query.periodMonth != null) params.set("periodMonth", String(query.periodMonth));
    if (query.status != null) params.set("status", query.status);
    if (query.employeeId != null) params.set("employeeId", query.employeeId);
    if (query.search != null) params.set("search", query.search);
    const q = params.toString();
    return fetcher.get<ListOvertimeRequestsResult>(q ? `${base}/requests?${q}` : `${base}/requests`);
  },

  getRequestsSummary(): Promise<OvertimeRequestSummaryResult> {
    return fetcher.get<OvertimeRequestSummaryResult>(`${base}/requests/summary`);
  },

  getRequestById(id: string): Promise<OvertimeRequestDetailDTO> {
    return fetcher.get<OvertimeRequestDetailDTO>(`${base}/requests/${id}`);
  },

  createRequest(dto: CreateOvertimeRequestDTO): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(`${base}/requests`, dto);
  },

  updateRequest(id: string, dto: UpdateOvertimeRequestDTO): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/requests/${id}`, dto);
  },

  listApprovals(query: ListOvertimeRequestsQuery): Promise<ListOvertimeRequestsResult> {
    const params = new URLSearchParams();
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.sort != null) params.set("sort", JSON.stringify(query.sort));
    if (query.periodYear != null) params.set("periodYear", String(query.periodYear));
    if (query.periodMonth != null) params.set("periodMonth", String(query.periodMonth));
    if (query.status != null) params.set("status", query.status);
    if (query.employeeId != null) params.set("employeeId", query.employeeId);
    if (query.search != null) params.set("search", query.search);
    const q = params.toString();
    return fetcher.get<ListOvertimeRequestsResult>(q ? `${base}/approvals?${q}` : `${base}/approvals`);
  },

  getApprovalSummary(query?: {
    periodYear?: number;
    periodMonth?: number;
  }): Promise<ApprovalSummaryResult> {
    const params = new URLSearchParams();
    if (query?.periodYear != null) params.set("periodYear", String(query.periodYear));
    if (query?.periodMonth != null) params.set("periodMonth", String(query.periodMonth));
    const q = params.toString();
    return fetcher.get<ApprovalSummaryResult>(
      q ? `${base}/approvals/summary?${q}` : `${base}/approvals/summary`,
    );
  },

  approveRequest(id: string): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/requests/${id}/approve`, {});
  },

  rejectRequest(id: string, rejectionReason?: string): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/requests/${id}/reject`, {
      rejectionReason: rejectionReason ?? undefined,
    });
  },

  bulkApprove(ids: string[]): Promise<{ updated: number }> {
    return fetcher.post<{ updated: number }>(`${base}/approvals/bulk-approve`, { ids });
  },

  bulkReject(ids: string[], rejectionReason?: string): Promise<{ updated: number }> {
    return fetcher.post<{ updated: number }>(`${base}/approvals/bulk-reject`, {
      ids,
      rejectionReason: rejectionReason ?? undefined,
    });
  },

  getSummaryByMonth(year: number, month: number): Promise<OTSummaryByMonthResult> {
    return fetcher.get<OTSummaryByMonthResult>(
      `${base}/summary?year=${year}&month=${month}`,
    );
  },
};
